import { evaluationSubtotalCents } from "@/lib/bag-coupon"
import { isCouponAvailableNow } from "@/lib/coupon-availability"
import type { CouponTerms } from "@/lib/coupon-readiness"
import { evaluateCoupons, evaluateCouponsPublic, PUBLIC_EVALUATION_LIMITS } from "@/lib/discount-evaluation/api"
import type { CouponEvaluation, EvaluateCouponsRequest, RejectedCoupon } from "@/lib/discount-evaluation/types"

/**
 * A bag verdict: coupon-service's, through the customer's session or the
 * public preview for a visitor without one; an estimate from the public
 * record only when the preview cannot be reached. Never sent with the order:
 * the checkout revalidates every code.
 */
export type BagEvaluation = { evaluation: CouponEvaluation }

/** What {@link evaluateBagCoupons} needs: the request checkout makes, whether there is a session, and the public terms read so far. */
export type BagEvaluationInput = {
  merchantId: string
  request: EvaluateCouponsRequest
  authenticated: boolean
  terms: Readonly<Record<string, CouponTerms | null | undefined>>
  now: Date
  signal: AbortSignal
}

function rejection(code: string, reason: string): RejectedCoupon {
  return { code, reason, violations: [] }
}

function estimatedCents(terms: CouponTerms, subtotalCents: number): number {
  const { reward, maxDiscountCents } = terms
  const raw =
    reward.kind === "percent" ? Math.round((subtotalCents * reward.basisPoints) / 10000) : reward.kind === "amount" ? reward.cents : 0
  const capped = maxDiscountCents > 0 ? Math.min(raw, maxDiscountCents) : raw
  return Math.max(0, Math.min(capped, subtotalCents))
}

/**
 * A visitor-side estimate for plain order coupons only (one rule, no
 * conditions, a share or an amount off): their minimum, expiry and weekly
 * window are read from the public record and the best one is kept. Anything
 * else is left out, so the bag says the checkout calculates it.
 */
export function estimateCouponEvaluation(
  request: EvaluateCouponsRequest,
  terms: Readonly<Record<string, CouponTerms | null | undefined>>,
  now: Date,
): CouponEvaluation {
  const subtotalCents = evaluationSubtotalCents(request.lines)
  const rejected: RejectedCoupon[] = []
  let best: { code: string; cents: number } | null = null
  for (const code of request.couponCodes) {
    const coupon = terms[code]
    if (!coupon || coupon.conditional || coupon.benefit.kind !== "order") continue
    if (coupon.expiresAt && Date.parse(coupon.expiresAt) <= now.getTime()) {
      rejected.push(rejection(code, "EVALUATION_REASON_EXPIRED"))
      continue
    }
    if (coupon.availability && !isCouponAvailableNow(coupon.availability, now)) {
      rejected.push(rejection(code, "EVALUATION_REASON_OUTSIDE_AVAILABILITY"))
      continue
    }
    if (coupon.minPurchaseCents > subtotalCents) {
      rejected.push(rejection(code, "EVALUATION_REASON_MIN_PURCHASE_NOT_MET"))
      continue
    }
    const cents = estimatedCents(coupon, subtotalCents)
    if (cents > 0 && (!best || cents > best.cents)) best = { code, cents }
  }
  if (!best) return { applied: [], rejected, allocations: [], totalDiscountCents: 0, maxCouponsPerOrder: 1 }
  return {
    applied: [{ code: best.code, couponId: "", ruleId: "", class: "COUPON_CLASS_ORDER", discountCents: best.cents }],
    rejected,
    allocations: [{ couponCode: best.code, ruleId: "", target: "ALLOCATION_TARGET_CART", lineId: "", optionLineId: "", amountCents: best.cents }],
    totalDiscountCents: best.cents,
    maxCouponsPerOrder: 1,
  }
}

const PUBLIC_CACHE_SIZE = 20

const publicCache = new Map<string, CouponEvaluation>()

function remember(key: string, evaluation: CouponEvaluation): CouponEvaluation {
  publicCache.delete(key)
  publicCache.set(key, evaluation)
  const oldest = publicCache.keys().next().value
  if (publicCache.size > PUBLIC_CACHE_SIZE && oldest !== undefined) publicCache.delete(oldest)
  return evaluation
}

async function previewForVisitor(input: BagEvaluationInput): Promise<CouponEvaluation> {
  const { merchantId, request, terms, now, signal } = input
  const body = {
    couponCodes: request.couponCodes.slice(0, PUBLIC_EVALUATION_LIMITS.codes),
    lines: request.lines,
    deliveryFeeCents: request.deliveryFeeCents,
  }
  if (body.lines.length > PUBLIC_EVALUATION_LIMITS.lines) return estimateCouponEvaluation(request, terms, now)
  const key = `${merchantId}:${JSON.stringify(body)}`
  const cached = publicCache.get(key)
  if (cached) return cached
  try {
    return remember(key, await evaluateCouponsPublic(merchantId, body, signal))
  } catch (error) {
    if (signal.aborted) throw error
    return estimateCouponEvaluation(request, terms, now)
  }
}

/** The single seam the bag asks for a coupon verdict: coupon-service with the session, its public preview without one (an estimate only if the preview is unreachable). */
export async function evaluateBagCoupons(input: BagEvaluationInput): Promise<BagEvaluation> {
  if (input.authenticated) return { evaluation: await evaluateCoupons(input.merchantId, input.request, input.signal) }
  return { evaluation: await previewForVisitor(input) }
}
